import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Clock, FileText, CheckCircle, XCircle, Play } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const apiClient = axios.create({ baseURL: API });
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const SimulacrosDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [simulacros, setSimulacros] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await apiClient.get('/simulacros');
        setSimulacros(res.data);
      } catch (err) {
        toast.error('Error al cargar simulacros');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 py-4 px-4 z-50 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-gray-600 hover:text-black transition-colors"
            data-testid="back-to-menu"
          >
            <ArrowLeft size={20} className="mr-2" />
            <span className="text-sm font-medium">Menú Principal</span>
          </button>
          <button
            onClick={onLogout}
            className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Salir
          </button>
        </div>
      </header>

      <div className="pt-20 pb-12 px-4 max-w-4xl mx-auto">
        <h1 className="text-3xl sm:text-4xl font-bold text-black text-center mb-2" data-testid="simulacros-title">
          Simulacros
        </h1>
        <p className="text-gray-500 text-center mb-2 text-sm">
          Practica con examenes simulacro de 280 preguntas en 5 horas
        </p>
        <p className="text-xs text-gray-400 text-center mb-10">Simulacion con fines exclusivamente educativos.</p>

        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin w-8 h-8 border-2 border-gray-300 border-t-[#1a5276] rounded-full mx-auto mb-4"></div>
            <p className="text-gray-500">Cargando simulacros...</p>
          </div>
        ) : simulacros.length === 0 ? (
          <div className="text-center py-16">
            <FileText size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg">No hay simulacros disponibles</p>
            <p className="text-gray-400 text-sm mt-1">El administrador debe cargar un simulacro primero</p>
          </div>
        ) : (
          <div className="space-y-4">
            {simulacros.map((sim) => {
              const attempt = sim.user_attempt;
              const isCompleted = attempt?.status === 'completed';
              const isInProgress = attempt?.status === 'in_progress';

              return (
                <div
                  key={sim.id}
                  className="border border-gray-200 rounded-xl p-6 hover:border-gray-400 transition-all"
                  data-testid={`simulacro-card-${sim.id}`}
                >
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{sim.title}</h3>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <FileText size={14} /> {sim.total_questions} preguntas
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={14} /> 5 horas
                        </span>
                        <span>{sim.total_cases} casos clínicos</span>
                      </div>
                      {isCompleted && (
                        <div className="mt-2 flex items-center gap-2">
                          {Math.round((attempt.score / attempt.total_questions) * 100) >= 60 ? (
                            <>
                              <CheckCircle size={16} className="text-green-600" />
                              <span className="text-sm font-semibold text-green-700">
                                Aprobado: {attempt.score}/{attempt.total_questions} ({Math.round((attempt.score / attempt.total_questions) * 100)}%)
                              </span>
                            </>
                          ) : (
                            <>
                              <XCircle size={16} className="text-red-500" />
                              <span className="text-sm font-semibold text-red-600">
                                No aprobado: {attempt.score}/{attempt.total_questions} ({Math.round((attempt.score / attempt.total_questions) * 100)}%)
                              </span>
                            </>
                          )}
                        </div>
                      )}
                      {isInProgress && (
                        <div className="mt-2 flex items-center gap-2">
                          <Clock size={16} className="text-yellow-600" />
                          <span className="text-sm font-semibold text-yellow-700">En progreso</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {isCompleted && attempt.score / attempt.total_questions >= 0.6 && (
                        <button
                          onClick={() => navigate(`/simulacros/${sim.id}?mode=results`)}
                          data-testid={`results-simulacro-${sim.id}`}
                          className="flex items-center gap-2 px-5 py-3 rounded-lg font-semibold transition-colors bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
                        >
                          <CheckCircle size={18} /> Ver Resultados
                        </button>
                      )}
                      <button
                        onClick={() => navigate(`/simulacros/${sim.id}`)}
                        data-testid={`start-simulacro-${sim.id}`}
                        className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold transition-colors ${
                          isInProgress
                            ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                            : 'bg-[#1a5276] text-white hover:bg-[#154360]'
                        }`}
                      >
                        <Play size={18} />
                        {isInProgress ? 'Continuar' : 'Iniciar'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SimulacrosDashboard;
